import { defineStore } from 'pinia'
import { fetchEnrollments as svcFetchEnrollments, createEnrollment as svcCreateEnrollment } from '@/services/enrollments'
import { getAccessToken, getSessionVersion } from '@/auth/storage'

export const useEnrollmentsStore = defineStore('enrollments', {
  state: () => ({
    enrollments: [],
    total: 0,
    page: 1,
    pageSize: 20,
    loaded: false,
    loading: false,
    error: null,
    lastLoadedAt: 0,
  }),
  getters: {
    all: (state) => state.enrollments,
    getByDrfCourseId: (state) => (drfCourseId) => {
      const target = String(drfCourseId)
      return state.enrollments.find(e => String(e.course) === target) || null
    },
  },
  actions: {
    async loadIfNeeded(force = false) {
      const version = getSessionVersion()
      const now = Date.now()
      const TTL = 5 * 60 * 1000 // 5 minutes
      if (!force && this.loaded && this.lastLoadedAt && (now - this.lastLoadedAt) < TTL) return
      const token = getAccessToken()
      if (!token) {
        this.enrollments = []
        this.loaded = true
        this.lastLoadedAt = now
        return
      }
      this.loading = true
      this.error = null
      try {
        // Cache-bust on force to avoid stale HTTP cached response
        const params = force ? { cb: Date.now() } : {}
        const data = await svcFetchEnrollments(params)
        if (version !== getSessionVersion()) return
        if (Array.isArray(data)) {
          this.enrollments = data
          this.total = data.length
        } else {
          this.enrollments = data?.results || []
          this.total = data?.count || this.enrollments.length
        }
        this.lastLoadedAt = Date.now()
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
        if (version === getSessionVersion()) this.loaded = true
      }
    },
    async loadPage(page = 1, pageSize = null, extra = {}) {
      const version = getSessionVersion()
      this.loading = true
      this.error = null
      try {
        const size = pageSize ?? this.pageSize
        const query = { page: String(page), page_size: String(size) }
        Object.entries(extra || {}).forEach(([k, v]) => {
          if (k === 'cacheBust') return
          if (v !== undefined && v !== null && v !== '') query[k] = String(v)
        })
        if (extra && extra.cacheBust) query.cb = String(Date.now())
        const data = await svcFetchEnrollments(query)
        if (version !== getSessionVersion()) return
        if (Array.isArray(data)) {
          this.enrollments = data
          this.total = data.length
        } else {
          this.enrollments = data?.results || []
          this.total = data?.count || this.enrollments.length
        }
        this.page = page
        this.pageSize = size
        this.lastLoadedAt = Date.now()
      } catch (e) {
        this.error = e
        throw e
      } finally {
        this.loading = false
        if (version === getSessionVersion()) this.loaded = true
      }
    },
    async enroll(drfCourseId) {
      // Attempt to enroll the current user into a DRF course id
      try {
        const created = await svcCreateEnrollment(drfCourseId)
        if (created && (created.payment_required || typeof created.id === 'undefined' || created.id === null)) {
          return created
        }
        // Upsert into local cache
        const idx = this.enrollments.findIndex(e => e.id === created.id)
        if (idx >= 0) this.enrollments[idx] = created
        else this.enrollments.push(created)
        this.lastLoadedAt = Date.now()
        this.loaded = true
        return created
      } catch (e) {
        this.error = e
        throw e
      }
    },
    clear() {
      this.enrollments = []
      this.total = 0
      this.page = 1
      this.pageSize = 20
      this.loaded = false
      this.loading = false
      this.error = null
      this.lastLoadedAt = 0
    }
  },
  persist: false
})
